import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { loginWithPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginWithPassword(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-zinc-50" data-testid="admin-login">
      <div className="lg:col-span-3 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-12" data-testid="login-brand">
            <img src="/appistry-logo.png" alt="Appistry" className="h-10 w-10 object-contain" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry</span>
          </Link>

          <p className="overline mb-4">Admin workbench</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-none mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign in.</h1>
          <p className="text-zinc-600 mb-10">Only authorised Appistry staff have access here.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-semibold text-zinc-800 mb-2 block">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--appistry-signal))]" data-testid="login-email" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-800 mb-2 block">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--appistry-signal))]" data-testid="login-password" />
            </div>
            <button type="submit" disabled={submitting} className="btn-signal w-full justify-center disabled:opacity-60" data-testid="login-submit">
              {submitting ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px bg-zinc-200 flex-1" />
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">or</span>
            <div className="h-px bg-zinc-200 flex-1" />
          </div>

          <button
            onClick={googleSignIn}
            className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors"
            data-testid="login-google"
          >
            <GoogleLogo size={18} weight="bold" /> Sign in with Google
          </button>
          <p className="text-xs text-zinc-500 mt-3">Google sign-in only works for Google accounts that already exist in the admin users table.</p>

          <div className="mt-10 text-xs font-mono text-zinc-500">
            <Link to="/" className="hover:text-zinc-900">← Back to Appistry</Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-2 bg-zinc-950 text-white p-10 relative overflow-hidden">
        <div className="absolute inset-0 grid-dots opacity-5" />
        <div className="relative h-full flex flex-col justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-10">APPISTRY // WORKBENCH</p>
            <h2 className="text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>The other side of the brochure.</h2>
            <p className="text-zinc-400 leading-relaxed">Everything in here is real — leads, consultations, demos, promotions, audit trails. No test data, no fluff.</p>
          </div>
          <p className="text-xs font-mono text-zinc-500">v1.0 · appistry.tech</p>
        </div>
      </div>
    </div>
  );
}
